<!-- 
Includes debounced input handling for optimized search.
Dropdown menu for refined user interaction.
Merge player functionality for specific user roles.
-->

<template>
  <v-row justify="center" class="search_widget_container ma-0" v-scroll="handleScroll">
    <v-col cols="12" class="pa-0 ms-0">
      <v-card variant="text" v-click-outside="handleClickedOutside" :class="{ 'absolute-results': addPlayer }">
        <div class="tech_search_container d-flex align-center px-3" style="height: 50px">
          <SvgIcon icon="search" class="search_icon" alt="Player Search Widget" />
          <input class="tech_search_input flex-grow-1 ml-2" v-model="userName" type="text" ref="searchInput" :placeholder="inputPlaceholder" />

          <!-- Clear button -->
          <v-btn density="compact" elevation="0" size="default" variant="plain" position="absolute" style="transform: translateX(-15px); right: 0" v-if="userName" icon @click="userName = ''">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!--  Added teleport to move the results to the body when search is in the nav  -->
        <Teleport to="body" :disabled="props.disableTeleport">
          <v-list v-if="menuVisible" elevation="0" class="tech_search_list" :class="hideMe && 'hide-results'">
            <!-- Skeleton Loader -->
            <div class="tech_search_skeleton" v-if="(loading || !dataReady) && userName.trim().length > 0">
              <v-skeleton-loader v-for="n in 1" :key="n" class="mx-auto my-2" type="avatar, sentences" />
            </div>

            <!-- Virtual Scroller for Content -->
            <div v-if="dataReady" class="tech_search_panel">
              <v-virtual-scroll :items="formattedResponse">
                <template v-slot="{ item }">
                  <v-card class="tech_search_card mx-auto px-2 py-2" color="#fff" theme="light" rel="noopener" @click="handleClick(item)">
                    <v-list-item class="w-100 px-1">
                      <span class="d-flex" style="justify-content: space-between; align-items: center">
                        <div class="d-flex" style="gap: 15px">
                          <v-avatar class="tech_search_avatar" :image="getResourceUrl('/' + item.photo)" size="45"></v-avatar>
                          <span>
                            <v-list-item-title class="tech_search_title">
                              {{ item.player ? item.player : item.club }}
                              <span v-if="item.category && item.category !== 'Senior'">&nbsp;({{ item.category }})</span>
                            </v-list-item-title>
                            <v-list-item-subtitle class="tech_search_subtitle">{{ item.country }} - {{ item.club }}, {{ item.county }}</v-list-item-subtitle>
                          </span>
                        </div>
                        <div class="justify-self-end">
                          <span class="tech_search_level">
                            {{ parseInt(item.level) === -1 ? 'TBD' : item.level }}
                          </span>
                        </div>
                      </span>
                    </v-list-item>
                  </v-card>
                </template>
              </v-virtual-scroll>

              <!-- Suggestion_action_div displayed to specific roles -->
              <v-card variant="flat" v-if="showCard1 && !addNewPlayer" class="tech_search_suggestion mx-auto">
                <div class="tech_suggestion_text">Looking for someone and can't find them?</div>
                <Button label="Learn more" version="primary" :slim="true" class="py-2" @click="handleLearnMore" rel="noopener noreferrer" />
              </v-card>

              <!-- Suggestion_action_div displayed to specific roles -->
              <v-card variant="flat" v-if="addNewPlayer" class="tech_search_suggestion mx-auto new_player_card">
                <div class="tech_suggestion_text">Looking for someone and can't find them?</div>
                <Button label="Learn more" version="primary" :slim="true" class="py-2" @click="handleLearnMore" rel="noopener noreferrer" />
                <Button label="Add New Player" version="primary" :slim="true" @click="handleAddNewPlayer" />
              </v-card>

              <!-- Merge_action_div displayed to specific roles -->
              <v-card v-if="showCard2" variant="flat" class="tech_search_suggestion mx-auto">
                <div class="tech_suggestion_text">Admin: Click to request a merge for these players.</div>
                <Button label="Request Merge" version="primary" :slim="true" class="py-2" @click="requestMerge" />
              </v-card>
            </div>
          </v-list>
        </Teleport>
      </v-card>
    </v-col>
  </v-row>
  <ModalPopup
    v-model="showModal"
    :title="modalTitle"
    :pageComponent="dynamicComponent"
    :pageComponentProps="modalProps"
    @update:model-value="handleModalUpdate"
    @action="handleModalAction"
    class="are-you-sure-modal-add-new-player" />
</template>

<script setup lang="ts">
import router from '@/router';
import debounce from 'lodash/debounce';
import Button from '@/components/ComponentLibrary/Button.vue';
import ModalPopup from '@/components/ComponentLibrary/ModalPopup.vue';
import AreYouSure from '@/components/ComponentLibrary/AreYouSure.vue';
import SvgIcon from '@/components/GlobalComponents/SvgIcon.vue';

import { ref, watch, computed, onMounted } from 'vue';
import { axiosLevelTech } from '@/services/axiosConfig';
import { submitMergeRequest } from '@/services/tools/SubmitMergeRequest';
import { getResourceUrl } from '@/services/admin_actions/apiUtils';
import { addNewPlayerToSite, AddNewPlayerParams } from '@/services/boxes/playerServices';

const props = defineProps({
  disableTeleport: { type: Boolean, default: true },
  addPlayer: { type: Boolean, default: false },
  addNewPlayer: { type: Boolean, default: false },
  includeClubs: { type: Boolean, default: true },
});

const searchInput = ref<HTMLInputElement | null>(null);
const focusInput = () => {
  if (searchInput.value) {
    searchInput.value.focus();
  }
};

const emit = defineEmits(['add-player', 'cleared']);

// Log the admin level variables when the component is mounted
// onMounted(() => {
//   console.group('Admin Level Variables');
//   console.log('admin:', window.admin);
//   console.log('results_admin:', window.results_admin);
//   console.log('county_admin:', window.county_admin);
//   console.log('squashskills_staff:', window.squashskills_staff);
//   console.groupEnd();
// });

// Define computed properties for user roles
const userRoles = computed(() => ({
  admin: (window as any).admin,
  resultsAdmin: (window as any).results_admin,
  countyAdmin: (window as any).county_admin,
  squashskillsStaff: (window as any).squashskills_staff,
}));

// User Role Types (for reference)
// Admin Role = userRoles.value.admin
// Result Admin = userRoles.value.resultsAdmin
// County Admin = userRoles.value.countyAdmin
// SquashSkills = userRoles.value.squashskillsStaff
// Add additional conditions as needed using || for 'OR', && for 'AND'

// Computed property to check if the search results include club results
const hasClubResult = computed(() => {
  return searchResponse.value?.some((item) => !item.player || parseInt(item.playerid) <= 0) ?? false;
});

// Computed property to show Card 1 (Suggest Card) to the following roles and regular users
const showCard1 = computed(() => {
  const isRestricted = userRoles.value.admin || userRoles.value.resultsAdmin || userRoles.value.countyAdmin || userRoles.value.squashskillsStaff;
  const resultCount = searchResponse.value?.length || 0;
  return !isRestricted || resultCount <= 1 || hasClubResult.value;
});

// Function to handle the Learn More button click
const handleLearnMore = () => {
  window.open('https://support.squashlevels.com/hc/en-us/articles/7713018013853-I-can-t-find-a-player-on-the-system', '_blank');
};

// Computed property to show Card 2 (Merge Card) to the following roles
const showCard2 = computed(() => {
  const isAdmin = userRoles.value.admin || userRoles.value.resultsAdmin || userRoles.value.countyAdmin;
  return isAdmin && !!searchResponse.value && searchResponse.value.length >= 2 && !hasClubResult.value;
});

const addNewPlayer = computed(() => {
  return props.addNewPlayer && userName.value.length >= 2;
});

// Define the structure of each player's data
interface PlayerData {
  photo: string;
  category: string;
  player: string;
  country: string;
  club: string;
  county: string;
  level: string;
  playerid: string;
  clubid: string;
  url?: string;
}

// Define the structure of the API response
interface ApiResponse {
  data: {
    array: PlayerData[];
  };
}

const searchResponse = ref<PlayerData[] | null>(null); // Holds the response from the API
const formattedResponse = computed((): readonly PlayerData[] | undefined => {
  // Computed property to format the data received from the API
  if (searchResponse.value) {
    return formatData(searchResponse.value) as readonly PlayerData[];
  }
  return undefined;
});
const loading = ref(false); // A flag to indicate the loading state
const userName = ref(''); // Holds the user input
const menuVisible = ref(false); // Controls visibility of the dropdown menu
const dataReady = ref(false); // Indicates if the data is ready to be displayed

// Reactive state for managing user feedback
const searchResults = ref<PlayerData[]>([]); // Use PlayerData interface to type the reactive reference

// Debounced function to fetch data after a specific delay to reduce API calls
const debouncedFetchData = debounce(() => {
  if (userName.value.trim()) {
    fetchData();
  }
}, 300); // 300ms debounce time

watch(userName, (newValue, oldValue) => {
  // Watcher to react to changes in the 'userName' ref
  const lengthDifference = Math.abs(newValue.length - oldValue.length);

  if (newValue.trim() === '') {
    // Reset everything if the input is cleared
    searchResponse.value = null;
    dataReady.value = false;
    menuVisible.value = false; // Hide the menu if the input is empty
  } else {
    if (newValue.length > 0 && menuVisible.value === false) {
      menuVisible.value = true; // Show the menu once the user starts typing
    }
    if (lengthDifference >= 3 || oldValue === '') {
      fetchData(); // Fetch data if the input length changed significantly or if it's the first input
    } else {
      debouncedFetchData(); // Otherwise, use the debounced version
    }
  }

  // console.log(`Input change: Old: ${oldValue}, New: ${newValue}, Length Diff: ${lengthDifference}`);
});

async function fetchData() {
  // Function to fetch data from the API
  // console.log('Fetching data for:', userName.value);
  loading.value = true;
  dataReady.value = false;

  try {
    // Making the POST request to the API
    const postData = { name: userName.value.toLowerCase(), includeClubs: props.includeClubs };
    const response = await axiosLevelTech.post<ApiResponse>('/api/search', postData);
    searchResponse.value = response.data.data.array; // Storing the response
    // console.log('Data fetched successfully:', searchResponse.value);
    dataReady.value = true; // Indicate that the data is ready
  } catch (error) {
    // Handling any errors during the fetch
    console.error('API call failed:', error);
    searchResponse.value = null;
  } finally {
    // Reset the loading state
    loading.value = false;
    // console.log('Fetch process complete, loading set to false');
  }
}

// Updated formatData function to include club results
// Function to format the data received from the API
function formatData(data: PlayerData[] | null): PlayerData[] | null {
  if (!data) return null;
  return data.map((item) => ({
    photo: item.photo,
    category: item.category,
    player: item.player,
    country: item.country,
    club: item.club,
    county: item.county,
    level: item.level,
    playerid: item.playerid,
    clubid: item.clubid, // Ensure clubid is also mapped
    url: parseInt(item.playerid) > 0 ? `/player_detail?player=${item.playerid}&show=last10` : `/dashboard?tab=community&community=200-${item.clubid}`,
  }));
}

// Watchers for loading and dataReady states to log their changes
watch(loading, (newLoadingValue) => {
  // console.log(`Loading: ${newLoadingValue}`);
});

watch(dataReady, (newDataReadyValue) => {
  // console.log(`Data Ready: ${newDataReadyValue}`);
});

// Assuming searchResults is a ref and already defined elsewhere in your setup
const requestMerge = async () => {
  // console.groupCollapsed('Merge Request Operation');
  // console.log('Starting Merge Request Operation');

  // Check if searchResponse.value is not null before mapping
  const playerIds = searchResponse.value ? searchResponse.value.map((player) => player.playerid) : [];
  // console.log('Collected Player IDs:', playerIds);

  if (playerIds.length > 0) {
    try {
      // console.log('Attempting to submit merge request...');
      await submitMergeRequest(playerIds); // Pass the array of IDs
      // console.log('Merge request submitted successfully.');

      // console.log('Operation Success:', operationSuccess.value);

      // Reset success message after a delay
      setTimeout(() => {
        // console.log('Resetting success state after timeout');
      }, 6000);
    } catch (error) {
      console.error('An error occurred during the merge request:', error);

      // Reset error state and message after a delay
      setTimeout(() => {
        console.log('Resetting error state and message after timeout');
      }, 5000);
    }
  } else {
    console.log('No player IDs collected. Aborting merge request.');
  }

  // console.groupEnd();
};

// adding a custom directive to close search results on click outside
const handleClickedOutside = () => {
  menuVisible.value = false;
};
const vClickOutside = {
  mounted(el: any, binding: any) {
    el.__ClickOutsideHandler__ = (event: Event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      } else {
        menuVisible.value = true;
      }
    };
    document.body.addEventListener('click', el.__ClickOutsideHandler__);
  },
  unmounted(el: any) {
    document.body.removeEventListener('click', el.__ClickOutsideHandler__);
  },
};

// function to hide the nav results when the navbar sides out of view on scroll
const scrollPosition = ref(0);
const hideMe = ref(false);
const handleScroll = () => {
  const currentScrollPosition = window.scrollY;
  if (currentScrollPosition > 299 && currentScrollPosition > scrollPosition.value) {
    hideMe.value = true;
    // console.log('hide results');
  } else {
    hideMe.value = false;
    // console.log('show results');
  }
  scrollPosition.value = window.scrollY;
};

const handleClick = (item: PlayerData) => {
  if (props.addPlayer) {
    emit('add-player', item);
    menuVisible.value = false;
  } else if (item.url) router.push(item.url);
};

const inputPlaceholder = computed(() => {
  if (props.addNewPlayer) {
    return 'Find a player or enter full name to add to site';
  }
  return props.includeClubs ? 'Find a player or club...' : 'Find a player...';
});

defineExpose({
  focusInput,
});

const addNewPlayerConfirm = () => {
  // console.log('Add New Player clicked in search widget', userName.value);
  const params: AddNewPlayerParams = { clubid: '', name: userName.value };
  addNewPlayerToSite(params).then((response) => {
    if (response.status === 'Success') {
      const player = { ...response.data, player: userName.value };
      emit('add-player', player);
      // console.log('Emmiting Add New Player response:', player);
      userName.value = '';
      showModal.value = false;
    }
  });
};

// Watch for clearing the search input
watch(userName, (val) => {
  if (val.trim() === '') {
    emit('cleared');
  }
});

const modalTitle = ref('');
const modalProps = ref({
  message: '',
  yesButtonLabel: '',
  noButtonLabel: '',
  action: '',
  yesButtonVersion: 'action',
  noButtonVersion: 'primary',
});
const showModal = ref(false);
const handleAddNewPlayer = () => {
  modalTitle.value = 'Add New Player';
  modalProps.value.message = `Please ensure "${capitalize(userName.value)}" is the correct player name to be added.`;
  modalProps.value.yesButtonLabel = 'Yes, Add Player';
  modalProps.value.noButtonLabel = 'No, Go Back';
  modalProps.value.action = 'addNewPlayerConfirm';
  showModal.value = true; // Show the modal when the button is clicked
};

const dynamicComponent = AreYouSure;

const handleModalUpdate = (value: boolean) => {
  showModal.value = value;
};

const handleModalAction = (action: string) => {
  if (action === 'addNewPlayerConfirm') {
    addNewPlayerConfirm();
  } else if (action === 'cancel') {
    showModal.value = false;
  }
};

const capitalize = (str: string) => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
</script>

<style lang="scss" scoped>
// Set Max Width For Input Field and extend to fill
.tech_search_input {
  width: 100%;
  padding: 10px;
  margin: 0px;
  height: 40px;
  margin: 0px;
  font-size: 18px;
  color: $muted-purple;
  font-family: $english-grotesque-font, sans-serif;
  background: transparent;
}

// General Styling For the Search Page Skeleton Loader
.tech_search_skeleton {
  background-color: $white;
  max-width: 600px;
  padding: 10px;
  border-radius: 5px;
  margin-top: 0px;
}

// General Styling For the Search Page Results Panel
.tech_search_panel {
  margin: 0px 0px;
  height: 100%;
  height: calc(100dvh - 70px);
  max-height: 400px;
  overflow: auto;
  border-radius: 5px;
}

// Remove Padding and Margin from Search Page List
.tech_search_list {
  padding: 0px !important;
  max-width: 500px;
  top: 100%;
  margin-top: 10px;
  background-color: transparent;
  border-radius: 4px;
  font-family: $english-grotesque-font, sans-serif;

  body > & {
    position: fixed;
    top: 55px;
    left: 75px;
    z-index: 2000;
    width: 100%;
    transition: transform 0.2s ease;

    &.hide-results {
      transform: translateY(-600px);
    }

    .v-btn.tech_primary_button {
      background-color: rgb(var(--v-theme-primary));
      &:hover {
        background-color: rgb(var(--v-theme-primary-hover));
      }
    }

    @media (max-width: $tablet-screen) {
      display: none;
    }
  }
}

// Remove Border Radius to the Search Page Cards
.tech_search_card {
  border-radius: 0px;
}

// Display Search Results Subtitle as flex
.tech_search_subtitle {
  display: flex;
  align-items: flex-start;
  text-align: left;
  text-align: start;
}

// Remove Margin from Search Page Results Avatar
.tech_search_avatar {
  margin: 0px;
  border-radius: 50px;
}

// Add Padding to Search Page Results Title
.tech_search_title {
  font-size: 20px;
  color: $primary-700;
}

// Increase Font Size of Search Page Results Subheading
.tech_search_level {
  font-size: 20px;
  color: $muted-purple;
}

// General Styling For the Search Page Suggestion Card
.tech_search_suggestion {
  border-radius: 0px 0px 5px 5px;
  background-color: rgb(var(--v-theme-highlight));
  padding: 0px 10px 10px 10px;
  @include display_flex_center;
  flex-direction: column;
  gap: 10px;
}

// Add Styling to the Search Page Suggestion Text
.tech_suggestion_text {
  width: 100%;
  font-size: 20px;
  font-family: $english-grotesque-font, sans-serif;
  line-height: 1.3;
  text-align: center;
  color: rgb(var(--v-theme-highlight-text));
  padding: 10px 10px 0px 10px;
}

// Styling for the Search Widget Container Div to allow for variable background color
.search_widget_container {
  // max-width: 500px;
  z-index: 1;
  width: 100%;
}

.tech_search_container {
  border-radius: 5px;
  width: 100%;
  background-color: #fff;
}

.absolute-results {
  display: block;
  position: relative;
  overflow: visible;
  z-index: 100;
  .tech_search_container {
    position: relative;
  }
  .tech_search_list {
    display: block;
    position: absolute;
    z-index: 1000;
    width: 100%;
    max-width: 500px;
  }
  .tech_search_skeleton {
    width: 100%;
    max-width: 500px;
  }
}

.new_player_card {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
  padding-bottom: 16px;

  .button__slim {
    min-width: 150px;
  }
  .tech_suggestion_text {
    font-size: 16px;
    line-height: 18px;

    @media (min-width: $tablet-screen) {
      font-size: 20px;
      line-height: 22px;
    }
  }
}

.search_icon {
  width: 28px;
  height: 25px;
  color: rgb(var(--v-theme-primary));
}
</style>
